/**
 * Blocked Address Configuration
 *
 * Addresses that cannot be shipped to (PO boxes, hotels, excluded territories, etc.)
 */

import { JAPAN_BLOCKED_ADDRESSES } from './japanBlockedAddresses';
import type { MelissaAddressData, ParsedAddress } from './types';

const PARCEL_LOCKER_PATTERNS = [/parcel locker/i, /荷物受取りロッカー/] as const;

const PO_BOX_PATTERNS = [/p\.?\s*o\.?\s*box/i, /post\s*office\s*box/i, /pobox/i, /私書箱/] as const;

const HOTEL_PATTERNS = [/hostels?/i, /hotels?/i, /motels?/i, /ホテル/, /ホステル/] as const;

// Split patterns keep each regex under Sonar complexity limits.
const BLOCKED_ADDRESS_PATTERNS = [
  ...PO_BOX_PATTERNS,
  ...HOTEL_PATTERNS,
  ...PARCEL_LOCKER_PATTERNS,
] as const;

/** Saleor-aligned codes for client-side blocked address validation. */
export const BLOCKED_ADDRESS_ERROR_CODES = {
  PO_BOX: 'shipping_address.po_box_not_supported',
  PARCEL_LOCKER: 'shipping_address.parcel_locker_not_supported',
  HOTEL: 'shipping_address.hotel_not_supported',
  MILITARY_BASE: 'shipping_address.military_base_not_supported',
} as const;

// US territories that cannot receive shipments
export const EXCLUDED_US_TERRITORIES = ['US-AS', 'US-GU', 'US-MP', 'US-PR', 'US-UM', 'US-VI'];

// Canadian territories that cannot receive shipments
export const EXCLUDED_CANADIAN_TERRITORIES = ['NT', 'NU', 'YT'];

// UK territories that cannot receive shipments
export const EXCLUDED_UK_TERRITORIES = ['JE', 'GY', 'IM'];

// Danish territories that cannot receive shipments
export const EXCLUDED_DANISH_TERRITORIES = ['GL', 'FO'];

// Finnish territories that cannot receive shipments
export const EXCLUDED_FINNISH_TERRITORIES = ['AX'];

// German postal codes that cannot receive shipments
export const EXCLUDED_GERMAN_POSTAL_CODES = ['78266', '27498'];

// French overseas postal codes (97xxx, 98xxx)
export const EXCLUDED_FRENCH_POSTAL_PREFIXES = ['97', '98'];

// Italian postal codes that cannot receive shipments
export const EXCLUDED_ITALIAN_POSTAL_CODES = ['22061', '23041', '00120', '22018', '22010', '21050'];
// Italian postal code ranges (San Marino: 47890-47899)
export const EXCLUDED_ITALIAN_POSTAL_RANGES = [{ start: 47890, end: 47899 }];

// Spanish postal codes that cannot receive shipments
export const EXCLUDED_SPANISH_POSTAL_CODES = ['51001', '52001'];
// Spanish postal code ranges (Canary Islands: 35xxx, 38xxx)
export const EXCLUDED_SPANISH_POSTAL_RANGES = [
  { start: 35000, end: 35999 },
  { start: 38000, end: 38999 },
];

function getMelissaAddressSearchText(address: MelissaAddressData): string {
  return [address.Address, address.Address1, address.DeliveryAddress].filter(Boolean).join(' ');
}

function matchesBlockedAddressPattern(value: string): boolean {
  return BLOCKED_ADDRESS_PATTERNS.some((pattern) => pattern.test(value));
}

function getPatternBlockedErrorCode(searchText: string): string | null {
  if (PARCEL_LOCKER_PATTERNS.some((pattern) => pattern.test(searchText))) {
    return BLOCKED_ADDRESS_ERROR_CODES.PARCEL_LOCKER;
  }

  if (PO_BOX_PATTERNS.some((pattern) => pattern.test(searchText))) {
    return BLOCKED_ADDRESS_ERROR_CODES.PO_BOX;
  }

  if (HOTEL_PATTERNS.some((pattern) => pattern.test(searchText))) {
    return BLOCKED_ADDRESS_ERROR_CODES.HOTEL;
  }

  return null;
}

function isJapanBlockedAddress(searchText: string): boolean {
  const japanBlockedAddressesPattern = new RegExp(JAPAN_BLOCKED_ADDRESSES.join('|'), 'i');
  if (japanBlockedAddressesPattern.test(searchText)) {
    return true;
  }
  return false;
}

function isExcludedSubdivision(subdivisionCode: string, administrativeArea: string): boolean {
  return (
    EXCLUDED_US_TERRITORIES.includes(subdivisionCode) ||
    EXCLUDED_CANADIAN_TERRITORIES.includes(administrativeArea) ||
    EXCLUDED_UK_TERRITORIES.includes(subdivisionCode) ||
    EXCLUDED_DANISH_TERRITORIES.includes(subdivisionCode) ||
    EXCLUDED_FINNISH_TERRITORIES.includes(subdivisionCode)
  );
}

function isPostalCodeInRanges(
  postalCode: string,
  ranges: ReadonlyArray<{ start: number; end: number }>
): boolean {
  const postalNum = Number.parseInt(postalCode, 10);
  return ranges.some((range) => postalNum >= range.start && postalNum <= range.end);
}

function isExcludedPostalCode(subdivisionCode: string, postalCode: string | undefined): boolean {
  if (!postalCode) {
    return false;
  }

  if (subdivisionCode === 'DE' && EXCLUDED_GERMAN_POSTAL_CODES.includes(postalCode)) {
    return true;
  }

  if (
    subdivisionCode === 'FR' &&
    EXCLUDED_FRENCH_POSTAL_PREFIXES.some((prefix) => postalCode.startsWith(prefix))
  ) {
    return true;
  }

  if (subdivisionCode === 'IT') {
    return (
      EXCLUDED_ITALIAN_POSTAL_CODES.includes(postalCode) ||
      isPostalCodeInRanges(postalCode, EXCLUDED_ITALIAN_POSTAL_RANGES)
    );
  }

  if (subdivisionCode === 'ES') {
    return (
      EXCLUDED_SPANISH_POSTAL_CODES.includes(postalCode) ||
      isPostalCodeInRanges(postalCode, EXCLUDED_SPANISH_POSTAL_RANGES)
    );
  }

  return false;
}

/**
 * Returns true when the user is searching for a blocked destination type.
 * Prevents PO box / hotel / motel suggestions from appearing in autocomplete.
 */
export function isBlockedSearchQuery(query: string): boolean {
  return matchesBlockedAddressPattern(query.trim());
}

/**
 * Check if a Melissa address suggestion should be excluded
 */
export function shouldExcludeAddress(address: MelissaAddressData): boolean {
  const searchText = getMelissaAddressSearchText(address);

  if (matchesBlockedAddressPattern(searchText) || isJapanBlockedAddress(searchText)) {
    return true;
  }

  const { CountrySubdivisionCode, PostalCode, AdministrativeArea } = address;

  return (
    isExcludedSubdivision(CountrySubdivisionCode, AdministrativeArea) ||
    isExcludedPostalCode(CountrySubdivisionCode, PostalCode)
  );
}

function getAddressSearchText(address: ParsedAddress): string {
  return [
    address.streetAddress1,
    address.streetAddress2,
    address.city,
    address.countryArea,
    address.postalCode,
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * Returns the shipping restriction code for a blocked form address, or null if allowed.
 */
export function getBlockedAddressErrorCode(address: ParsedAddress): string | null {
  const searchText = getAddressSearchText(address);
  const patternCode = getPatternBlockedErrorCode(searchText);

  if (patternCode) {
    return patternCode;
  }

  if (isJapanBlockedAddress(searchText)) {
    return BLOCKED_ADDRESS_ERROR_CODES.MILITARY_BASE;
  }

  return null;
}

/**
 * Check if a form address input should be blocked
 */
export function isBlockedAddress(address: ParsedAddress): boolean {
  return getBlockedAddressErrorCode(address) !== null;
}
